(() => {
  const root = window.Shopify?.routes?.root || '/';
  const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-BE', { style: 'currency', currency: window.Shopify?.currency?.active || 'EUR' }).format(cents / 100);
  const q = (s, c = document) => c.querySelector(s);
  const qa = (s, c = document) => [...c.querySelectorAll(s)];
  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (char) => ({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;'}[char]));

  const toast = (message) => {
    const el = q('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(el._timer);
    el._timer = setTimeout(() => el.classList.remove('is-visible'), 2800);
  };

  const designMode = Boolean(window.Shopify?.designMode);
  const revealObserver = !designMode && 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.07, rootMargin: '0px 0px -4% 0px' }) : null;
  const initializeReveals = (scope = document) => {
    const elements = qa('[data-reveal]', scope);
    if (scope.matches?.('[data-reveal]')) elements.unshift(scope);
    elements.forEach((el) => revealObserver ? revealObserver.observe(el) : el.classList.add('is-visible'));
  };
  initializeReveals();
  const showContactConfirmation = (scope = document) => {
    const confirmation = q('[data-contact-success]', scope);
    if (!confirmation) return;
    requestAnimationFrame(() => {
      confirmation.focus({ preventScroll: true });
      confirmation.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    });
  };
  showContactConfirmation();
  if (designMode) {
    document.addEventListener('shopify:section:load', (event) => showContactConfirmation(event.target));
    document.addEventListener('shopify:section:load', (event) => initializeReveals(event.target));
    document.addEventListener('shopify:section:select', (event) => initializeReveals(event.target));
  }

  qa('[data-auto-submit]').forEach((el) => el.addEventListener('change', () => el.form?.submit()));
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest?.('.footer-toggle');
    if (!toggle || !window.matchMedia('(max-width: 900px)').matches) return;
    toggle.setAttribute('aria-expanded', String(toggle.getAttribute('aria-expanded') !== 'true'));
  });

  const productAccordionAnimations = new WeakMap();
  document.addEventListener('click', (event) => {
    const summary = event.target.closest?.('.product-accordion > summary');
    if (!summary) return;
    const details = summary.parentElement;
    const panel = q('.product-accordion-panel', details);
    if (!panel?.animate) return;
    event.preventDefault();
    const running = productAccordionAnimations.get(details);
    const opening = running ? !running.opening : !details.open;
    const height = details.open ? panel.getBoundingClientRect().height : 0;
    running?.animation.cancel();
    details.open = true;
    panel.inert = !opening;
    summary.setAttribute('aria-expanded', String(opening));
    const animation = panel.animate(
      [{ height: `${height}px` }, { height: opening ? `${panel.scrollHeight}px` : '0px' }],
      { duration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300, easing: 'ease', fill: 'forwards' }
    );
    productAccordionAnimations.set(details, { animation, opening });
    animation.onfinish = () => {
      details.open = opening;
      panel.inert = false;
      summary.removeAttribute('aria-expanded');
      productAccordionAnimations.delete(details);
      animation.cancel();
    };
  });

  const menuButton = q('[data-menu-toggle]');
  const mobileMenu = q('[data-mobile-menu]');
  const closeMenu = (restoreFocus = false) => {
    if (!menuButton || !mobileMenu) return;
    menuButton.setAttribute('aria-expanded', 'false');
    mobileMenu.hidden = true;
    mobileMenu.inert = true;
    if (restoreFocus) menuButton.focus({ preventScroll: true });
  };
  menuButton?.addEventListener('click', () => {
    const open = menuButton.getAttribute('aria-expanded') === 'true';
    menuButton.setAttribute('aria-expanded', String(!open));
    mobileMenu.hidden = open;
    mobileMenu.inert = open;
    if (!open) q('a', mobileMenu)?.focus({ preventScroll: true });
  });
  qa('[data-mobile-menu] a').forEach((link) => link.addEventListener('click', () => closeMenu(false)));
  document.addEventListener('click', (event) => {
    if (!mobileMenu || mobileMenu.hidden || !menuButton) return;
    if (mobileMenu.contains(event.target) || menuButton.contains(event.target)) return;
    closeMenu(mobileMenu.contains(document.activeElement));
  }, true);

  const drawer = q('[data-cart-drawer]');
  const backdrop = q('[data-cart-backdrop]');
  const cartCopy = {
    empty: drawer?.dataset.emptyText || 'Your cart is empty.',
    emptyLink: drawer?.dataset.emptyLinkLabel || 'shop ALTO',
    remove: drawer?.dataset.removeLabel || 'remove',
    error: drawer?.dataset.errorText || 'We could not load the cart. Please refresh the page.'
  };
  const upsellProductsNode = q('[data-cart-upsell-products]', drawer);
  let upsellProducts = {};
  try { upsellProducts = JSON.parse(upsellProductsNode?.textContent || '{}'); } catch {}
  const actionCopy = window.CloudsCopy || {};
  let cartOpener = null;
  const updateCount = (count) => qa('[data-cart-count]').forEach((el) => el.textContent = count);
  const renderCartPrice = (original, final) => original > final
    ? `<s>${money(original)}</s><strong>${money(final)}</strong>`
    : `<span>${money(final)}</span>`;
  const renderLineProperties = (item) => Object.entries(item.properties || {})
    .filter(([name, value]) => value && !name.startsWith('_'))
    .map(([name, value]) => `<p>${escapeHtml(name)}: ${escapeHtml(value)}</p>`)
    .join('');
  const orderBundledItems = (items) => {
    const children = new Map();
    const regular = [];
    items.forEach((item) => {
      const parentVariantId = item.properties?._bundle_parent_variant_id;
      if (!parentVariantId) return regular.push(item);
      const key = String(parentVariantId);
      children.set(key, [...(children.get(key) || []), item]);
    });
    const ordered = [];
    regular.forEach((item) => {
      ordered.push(item);
      ordered.push(...(children.get(String(item.variant_id)) || []));
      children.delete(String(item.variant_id));
    });
    children.forEach((itemsForMissingParent) => ordered.push(...itemsForMissingParent));
    return ordered;
  };
  const closeUpsellPicker = () => {
    const picker = q('[data-upsell-picker]', drawer);
    const toggle = q('[data-upsell-toggle]', picker);
    const options = q('[data-upsell-options]', picker);
    picker?.classList.remove('is-open');
    toggle?.setAttribute('aria-expanded', 'false');
    options?.setAttribute('aria-hidden', 'true');
    if (options) options.inert = true;
  };
  document.addEventListener('click', (event) => {
    const picker = q('[data-upsell-picker]', drawer);
    if (picker?.classList.contains('is-open') && !picker.contains(event.target)) closeUpsellPicker();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const toggle = q('[data-upsell-toggle]', drawer);
    if (toggle?.getAttribute('aria-expanded') !== 'true') return;
    closeUpsellPicker();
    toggle.focus();
  });

  const renderCart = async () => {
    const holder = q('[data-cart-items]');
    if (!holder) return;
    try {
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Unable to load cart');
      const cart = await response.json();
      updateCount(cart.item_count);
      const footer = q('[data-cart-footer]');
      const upsell = q('[data-cart-upsell]');
      if (!cart.items.length) {
        holder.innerHTML = `<p>${escapeHtml(cartCopy.empty)}</p><a class="text-link" href="/collections/shop-alto">${escapeHtml(cartCopy.emptyLink)}</a>`;
        if (footer) footer.hidden = true;
        if (upsell) upsell.hidden = true;
        return;
      }
      holder.innerHTML = orderBundledItems(cart.items).map(item => `
        <div class="drawer-line">
          <a href="${item.url}"><img src="${item.image || ''}" alt="${escapeHtml(item.product_title)}"></a>
          <div>
            <h3><a href="${item.url}">${escapeHtml(item.product_title)}</a></h3>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<p>${escapeHtml(item.variant_title)}</p>` : ''}
            ${renderLineProperties(item)}
            <p class="cart-price-pair">${renderCartPrice(item.original_line_price, item.final_line_price)}</p>
            <div class="drawer-line-actions">
              <button type="button" data-cart-change="${item.key}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button>
              <span>${item.quantity}</span>
              <button type="button" data-cart-change="${item.key}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
              <button class="drawer-remove" type="button" data-cart-change="${item.key}" data-quantity="0">${escapeHtml(cartCopy.remove)}</button>
            </div>
          </div>
        </div>`).join('');
      q('[data-cart-total]').textContent = money(cart.total_price);
      const checkoutTotal = q('[data-cart-checkout-total]');
      if (checkoutTotal) checkoutTotal.textContent = money(cart.total_price);
      const originalTotal = q('[data-cart-original-total]');
      const savings = q('[data-cart-savings]');
      const saved = Math.max(0, cart.original_total_price - cart.total_price);
      if (originalTotal) {
        originalTotal.textContent = money(cart.original_total_price);
        originalTotal.hidden = saved === 0;
      }
      if (savings) {
        savings.hidden = saved === 0;
        q('[data-cart-savings-amount]', savings).textContent = money(saved);
      }
      if (footer) footer.hidden = false;
      const hasMini = cart.items.some((item) => item.handle.startsWith('mini-alto'));
      const hasAlto = cart.items.some((item) => item.handle.startsWith('alto'));
      if (upsell) {
        const targetFamily = hasMini && !hasAlto ? 'alto' : (hasAlto && !hasMini ? 'mini' : '');
        const earth = cart.items.some((item) => item.handle.endsWith('soft-earth'));
        const sourceItem = targetFamily === 'mini'
          ? cart.items.find((item) => item.handle.startsWith('alto'))
          : cart.items.find((item) => item.handle.startsWith('mini-alto'));
        const suggestion = targetFamily ? upsellProducts[targetFamily]?.[earth ? 'earth' : 'cotton'] : null;
        const availableVariants = suggestion?.variants?.filter((variant) => variant.available) || [];
        upsell.hidden = availableVariants.length === 0;
        if (availableVariants.length) {
          qa('[data-upsell-link]', upsell).forEach((link) => { link.href = suggestion.url; });
          const image = q('[data-upsell-image]', upsell);
          if (image) { image.src = suggestion.image; image.alt = suggestion.title; }
          q('[data-upsell-title]', upsell).textContent = suggestion.title;
          const price = q('[data-upsell-price]', upsell);
          price.textContent = money(availableVariants[0].price);
          const picker = q('[data-upsell-picker]', upsell);
          const toggle = q('[data-upsell-toggle]', picker);
          const selection = q('[data-upsell-selection]', picker);
          const options = q('[data-upsell-options]', picker);
          const optionsList = q('[data-upsell-options-list]', picker);
          selection.textContent = drawer.dataset.upsellCableLabel || 'choose cable color';
          optionsList.innerHTML = availableVariants.map((variant) => `<button type="button" data-upsell-variant="${variant.id}" data-price="${variant.price}" aria-pressed="false">${escapeHtml(variant.title)}</button>`).join('');
          closeUpsellPicker();
          const add = q('[data-upsell-add]', upsell);
          add.dataset.variantId = '';
          add.dataset.parentVariantId = sourceItem?.variant_id || '';
          add.disabled = true;
          add.textContent = drawer.dataset.upsellAddLabel || 'add';
          toggle.onclick = () => {
            const opening = toggle.getAttribute('aria-expanded') !== 'true';
            picker.classList.toggle('is-open', opening);
            toggle.setAttribute('aria-expanded', String(opening));
            options.setAttribute('aria-hidden', String(!opening));
            options.inert = !opening;
            if (opening) q('[data-upsell-variant]', options)?.focus();
          };
          qa('[data-upsell-variant]', optionsList).forEach((option) => option.onclick = () => {
            add.dataset.variantId = option.dataset.upsellVariant;
            add.disabled = false;
            selection.textContent = option.textContent;
            qa('[data-upsell-variant]', optionsList).forEach((item) => item.setAttribute('aria-pressed', String(item === option)));
            price.textContent = money(Number(option.dataset.price));
            closeUpsellPicker();
            toggle.focus();
          });
        }
      }
      qa('[data-cart-change]', holder).forEach((button) => button.addEventListener('click', async () => {
        button.disabled = true;
        await fetch(`${root}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: button.dataset.cartChange, quantity: Number(button.dataset.quantity) }) });
        await renderCart();
      }));
      const upsellAdd = q('[data-upsell-add]', upsell);
      if (upsellAdd) upsellAdd.onclick = async (event) => {
        const button = event.currentTarget;
        button.disabled = true;
        button.textContent = drawer.dataset.upsellAddingLabel || 'adding…';
        try {
          const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: Number(button.dataset.variantId), quantity: 1, properties: { _bundle_parent_variant_id: button.dataset.parentVariantId } }] }) });
          if (!response.ok) throw new Error('Unable to add this item');
          await renderCart();
        } catch (error) {
          button.disabled = false;
          button.textContent = drawer.dataset.upsellAddLabel || 'add';
          toast(error.message);
        }
      };
    } catch (error) {
      holder.innerHTML = `<p>${escapeHtml(cartCopy.error)}</p>`;
    }
  };

  const focusCartClose = () => q('[data-cart-close]')?.focus({ preventScroll: true });
  const openDrawer = async (event) => {
    if (event?.currentTarget) cartOpener = event.currentTarget;
    else if (document.activeElement instanceof HTMLElement) cartOpener = document.activeElement;
    drawer?.classList.add('is-open');
    drawer?.setAttribute('aria-hidden', 'false');
    if (backdrop) backdrop.hidden = false;
    document.body.classList.add('drawer-open');
    await new Promise(resolve => requestAnimationFrame(resolve));
    focusCartClose();
    await renderCart();
    focusCartClose();
  };
  const closeDrawer = (restoreFocus = false) => {
    drawer?.classList.remove('is-open');
    drawer?.setAttribute('aria-hidden', 'true');
    if (backdrop) backdrop.hidden = true;
    document.body.classList.remove('drawer-open');
    if (restoreFocus && cartOpener?.isConnected) cartOpener.focus({ preventScroll: true });
  };
  qa('[data-cart-open]').forEach((el) => el.addEventListener('click', openDrawer));
  document.addEventListener('click', (event) => {
    if (event.target.closest?.('[data-cart-close]') || event.target.matches?.('[data-cart-backdrop]')) closeDrawer(true);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const drawerWasOpen = drawer?.classList.contains('is-open');
    const menuWasOpen = menuButton?.getAttribute('aria-expanded') === 'true';
    closeDrawer(drawerWasOpen);
    closeMenu(menuWasOpen);
  });

  qa('[data-product-gallery]').forEach((gallery) => {
    const slides = qa('[data-gallery-slide]', gallery);
    const thumbs = qa('[data-gallery-thumb]', gallery);
    const current = q('[data-gallery-current]', gallery);
    if (!slides.length) return;
    let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));

    const show = (next) => {
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      thumbs.forEach((thumb, i) => {
        const active = i === index;
        thumb.classList.toggle('is-active', active);
        if (active) thumb.setAttribute('aria-current', 'true'); else thumb.removeAttribute('aria-current');
      });
      if (current) current.textContent = index + 1;
      thumbs[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    };
    if (current) current.textContent = index + 1;

    q('[data-gallery-prev]', gallery)?.addEventListener('click', () => show(index - 1));
    q('[data-gallery-next]', gallery)?.addEventListener('click', () => show(index + 1));
    thumbs.forEach((thumb) => thumb.addEventListener('click', () => show(Number(thumb.dataset.galleryThumb))));

    let touchStart = 0;
    gallery.addEventListener('touchstart', (event) => { touchStart = event.changedTouches[0]?.clientX || 0; }, { passive: true });
    gallery.addEventListener('touchend', (event) => {
      const delta = (event.changedTouches[0]?.clientX || 0) - touchStart;
      if (Math.abs(delta) > 45) show(index + (delta < 0 ? 1 : -1));
    }, { passive: true });

    gallery._showMediaId = (mediaId) => {
      const target = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
      if (target >= 0) show(target);
    };
  });

  qa('[data-product-root]').forEach((productRoot) => {
    const dataNode = q('[data-product-json]', productRoot);
    if (!dataNode) return;
    let product;
    try { product = JSON.parse(dataNode.textContent); } catch { return; }

    const isGift = productRoot.classList.contains('product-main--gift-card');
    const digitalGiftDataNode = q('[data-digital-gift-card-json]', productRoot);
    let digitalGiftProduct = null;
    if (digitalGiftDataNode) {
      try { digitalGiftProduct = JSON.parse(digitalGiftDataNode.textContent); } catch { digitalGiftProduct = null; }
    }
    const optionInputs = qa('[data-variant-picker] input', productRoot);
    const variantInput = q('[data-variant-id]', productRoot);
    const price = q('[data-current-price]', productRoot);
    const comparePrice = q('[data-compare-price]', productRoot);
    const addPrice = q('[data-add-price]', productRoot);
    const addButton = q('[data-add-button]', productRoot);
    const addLabel = q('[data-add-label]', productRoot);
    const status = q('[data-product-status]', productRoot);
    const statusText = {
      available: status?.dataset.availableText || 'made to order · dispatches in 4–7 business days',
      giftCard: status?.dataset.giftCardText || 'valid online at clouds-collective.com',
      unavailable: status?.dataset.unavailableText || 'currently unavailable'
    };
    const mobilePrice = q('[data-mobile-price]', productRoot);
    const mobileAdd = q('[data-mobile-add]', productRoot);
    const gallery = q('[data-product-gallery]', productRoot);
    const form = q('[data-product-form]', productRoot);
    const recipient = q('[data-gift-card-recipient]', productRoot);
    const recipientControls = recipient ? qa('[data-recipient-control]', recipient) : [];
    const recipientEmail = recipient ? q('[data-recipient-email]', recipient) : null;
    const recipientOffset = recipient ? q('[data-recipient-offset]', recipient) : null;
    const recipientSendOn = recipient ? q('[data-recipient-send-on]', recipient) : null;
    const labels = {
      add: productRoot.dataset.addText || 'Add to cart',
      soldOut: productRoot.dataset.soldOutText || 'Sold out',
      unavailable: productRoot.dataset.unavailableText || 'Unavailable'
    };
    let isAdding = false;

    if (recipientSendOn) {
      const toLocalDate = (date) => new Date(date.getTime() - (date.getTimezoneOffset() * 60_000)).toISOString().slice(0, 10);
      const today = new Date();
      const maxDate = new Date(today);
      maxDate.setDate(maxDate.getDate() + 90);
      recipientSendOn.min = toLocalDate(today);
      recipientSendOn.max = toLocalDate(maxDate);
    }

    const selectedOptions = () => qa('[data-variant-picker] fieldset', productRoot).map((group) => q('input:checked', group)?.value).filter(Boolean);
    const purchaseVariant = (selected) => {
      const baseVariant = product.variants.find((candidate) => candidate.options.every((option, i) => option === selected[i]));
      const deliveryIndex = product.options.findIndex((option) => option.toLowerCase() === 'type');
      const amountIndex = product.options.findIndex((option) => option.toLowerCase() === 'amount');
      const isDigitalSelection = deliveryIndex >= 0 && selected[deliveryIndex]?.toLowerCase() === 'digital';
      if (!isGift || !digitalGiftProduct || !isDigitalSelection || amountIndex < 0) return baseVariant;

      const amount = selected[amountIndex];
      return digitalGiftProduct.variants.find((candidate) => candidate.options.some((option) => option === amount)) || baseVariant;
    };
    const syncGiftRecipient = () => {
      if (!recipient) return;
      const deliveryGroup = q('[data-gift-card-delivery-option]', productRoot);
      const deliveryValue = q('input:checked', deliveryGroup)?.value || '';
      const isDigital = deliveryValue.toLowerCase() === 'digital';
      recipient.hidden = !isDigital;
      recipientControls.forEach((control) => { control.disabled = !isDigital; });
      if (recipientEmail) recipientEmail.required = isDigital;
      if (recipientOffset) recipientOffset.value = isDigital ? String(new Date().getTimezoneOffset()) : '';
    };

    const setOptionAvailability = () => {
      const groups = qa('[data-variant-picker] fieldset', productRoot);
      const priorSelections = [];
      groups.forEach((group, index) => {
        const inputs = qa('input', group);
        inputs.forEach((input) => {
          const available = product.variants.some((candidate) => candidate.available
            && candidate.options[index] === input.value
            && priorSelections.every((value, priorIndex) => value == null || candidate.options[priorIndex] === value));
          input.disabled = !available;
          input.setAttribute('aria-disabled', String(!available));
          const label = input.nextElementSibling;
          label?.classList.toggle('is-unavailable', !available);
          label?.setAttribute('aria-disabled', String(!available));
        });
        let checked = q('input:checked', group);
        if (!checked || checked.disabled) {
          const fallback = inputs.find((input) => !input.disabled);
          if (fallback) {
            fallback.checked = true;
            checked = fallback;
          }
        }
        priorSelections[index] = checked?.value;
      });
    };

    const syncVariant = () => {
      setOptionAvailability();
      const selected = selectedOptions();
      const variant = purchaseVariant(selected);
      if (!variant) {
        if (variantInput && variantInput.value) {
          variantInput.value = '';
          variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (price) price.textContent = '—';
        if (comparePrice) {
          comparePrice.textContent = '';
          comparePrice.hidden = true;
        }
        if (addPrice) addPrice.textContent = '';
        if (mobilePrice) mobilePrice.textContent = '—';
        if (addButton) addButton.disabled = true;
        if (mobileAdd) mobileAdd.disabled = true;
        if (addLabel) addLabel.textContent = labels.unavailable;
        if (mobileAdd) {
          mobileAdd.textContent = labels.unavailable;
          delete mobileAdd.dataset.variantId;
        }
        if (status) status.textContent = statusText.unavailable;
        syncGiftRecipient();
        const url = new URL(window.location.href);
        url.searchParams.delete('variant');
        window.history.replaceState({}, '', url);
        return;
      }
      if (variantInput && variantInput.value !== String(variant.id)) {
        variantInput.value = variant.id;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (price) price.textContent = money(variant.price);
      if (comparePrice) {
        const showComparePrice = variant.compare_at_price > variant.price;
        comparePrice.textContent = showComparePrice ? money(variant.compare_at_price) : '';
        comparePrice.hidden = !showComparePrice;
      }
      if (addPrice) addPrice.textContent = money(variant.price);
      if (mobilePrice) mobilePrice.textContent = money(variant.price);
      if (addButton) addButton.disabled = isAdding || !variant.available;
      if (mobileAdd) {
        mobileAdd.disabled = isAdding || !variant.available;
        mobileAdd.dataset.variantId = variant.id;
      }
      if (!isAdding) {
        if (addLabel) addLabel.textContent = variant.available ? labels.add : labels.soldOut;
        if (mobileAdd) mobileAdd.textContent = variant.available ? labels.add : labels.soldOut;
      }
      if (status) status.textContent = isGift ? statusText.giftCard : (variant.available ? statusText.available : statusText.unavailable);
      if (variant.featured_media?.id && gallery?._showMediaId) gallery._showMediaId(variant.featured_media.id);
      syncGiftRecipient();
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    };
    optionInputs.forEach((input) => input.addEventListener('change', syncVariant));

    const quantity = q('input[name="quantity"]', productRoot);
    const normalizeQuantity = () => {
      if (!quantity) return 1;
      const parsed = Number(quantity.value);
      const normalized = Number.isFinite(parsed) ? Math.max(1, Math.floor(parsed)) : 1;
      quantity.value = normalized;
      return normalized;
    };
    quantity?.addEventListener('change', normalizeQuantity);
    quantity?.addEventListener('blur', normalizeQuantity);
    q('[data-quantity-minus]', productRoot)?.addEventListener('click', () => { if (quantity) quantity.value = Math.max(1, normalizeQuantity() - 1); });
    q('[data-quantity-plus]', productRoot)?.addEventListener('click', () => { if (quantity) quantity.value = normalizeQuantity() + 1; });

    mobileAdd?.addEventListener('click', () => form?.requestSubmit());
    productRoot._syncPurchaseState = syncVariant;
    productRoot._setAdding = (adding) => { isAdding = adding; syncVariant(); };
    productRoot._normalizeQuantity = normalizeQuantity;
    syncVariant();
  });

  qa('[data-product-form]').forEach((form) => form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const button = q('[data-add-button]', form);
    const label = q('[data-add-label]', form);
    const rootEl = form.closest('[data-product-root]');
    const mobileButton = rootEl ? q('[data-mobile-add]', rootEl) : null;
    rootEl?._normalizeQuantity?.();
    rootEl?._syncPurchaseState?.();
    if (!q('[data-variant-id]', form)?.value || button?.disabled) return;
    rootEl?._setAdding?.(true);
    const addingLabel = actionCopy.adding || 'Adding…';
    const addedLabel = actionCopy.added || 'Added';
    if (label) label.textContent = addingLabel;
    if (mobileButton) mobileButton.textContent = addingLabel.toLowerCase();
    try {
      const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Unable to add this item');
      }
      if (label) label.textContent = addedLabel;
      if (mobileButton) mobileButton.textContent = addedLabel.toLowerCase();
      await openDrawer();
      setTimeout(() => {
        rootEl?._syncPurchaseState?.();
      }, 1200);
    } catch (error) {
      toast(error.message);
    } finally {
      rootEl?._setAdding?.(false);
    }
  }));
})();
